import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Toaster, toast } from "sonner";
import { Bell, BellOff, ChevronLeft, CheckCheck } from "lucide-react";
import { api, getUser } from "../lib/api";
import { Page, Spinner, EmptyState, Chip } from "../components/Brand";
import { timeAgoFr } from "../lib/fr";
import { subscribePush, unsubscribePush, isPushSubscribed } from "../lib/push";

const TYPE_LABEL = {
  created: "Nouvelle référence",
  scheduled: "Rendez-vous programmé",
  seen: "Patiente vue",
  missed: "Rendez-vous manqué",
  completed: "Référence terminée",
  need_info: "Informations demandées",
};

export default function Inbox() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = getUser();
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const { data: events, isLoading } = useQuery({
    queryKey: ["events"],
    queryFn: async () => (await api.get("/events")).data,
    refetchInterval: 20000,
  });

  useEffect(() => { isPushSubscribed().then(setPushOn); }, []);

  async function togglePush() {
    setPushBusy(true);
    try {
      if (pushOn) {
        await unsubscribePush();
        setPushOn(false);
        toast.success("Notifications push désactivées");
      } else {
        // Call subscribePush first so it can detect iOS-needs-install before checking permissions
        const res = await subscribePush();
        if (res.ok) {
          setPushOn(true);
          toast.success("Notifications push activées");
        } else if (res.reason === "ios-needs-install") {
          toast.error("Sur iPhone : ajoutez d'abord l'app à l'écran d'accueil (Partager → Sur l'écran d'accueil)");
        } else if (res.reason === "denied") {
          toast.error("Notifications bloquées dans le navigateur");
        } else if (res.reason === "unsupported") {
          toast.error("Push non supporté sur ce navigateur");
        } else {
          toast.error("Erreur d'activation");
        }
      }
    } catch (err) {
      toast.error("Erreur push: " + (err?.message || ""));
    } finally { setPushBusy(false); }
  }

  async function markAllRead() {
    await api.post("/events/mark-all-read");
    queryClient.invalidateQueries({ queryKey: ["events"] });
    queryClient.invalidateQueries({ queryKey: ["unread-count"] });
  }

  async function clickEvent(ev) {
    if (!ev.read) {
      await api.post(`/events/${ev.id}/read`).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["unread-count"] });
    }
    // navigate to referral
    if (user?.role === "obgyn") navigate(`/obgyn/${ev.referral_id}`);
    else if (user?.role === "im") navigate(`/referral/${ev.referral_id}`);
  }

  function backTo() {
    if (!user) return "/";
    return user.role === "im" ? "/im" : user.role === "obgyn" ? "/obgyn" : "/admin";
  }

  return (
    <Page>
      <Toaster position="top-center" richColors />
      <button onClick={() => navigate(backTo())} className="text-[14px] mb-4 inline-flex items-center gap-1 hover:underline"
              style={{ color: "var(--cs-text-muted)" }} data-testid="back-link">
        <ChevronLeft size={16} /> Retour
      </button>

      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="cs-label">Notifications</div>
          <h1 className="font-semibold mt-1" style={{ fontSize: 30, letterSpacing: "-0.6px" }}>Boîte de réception</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={togglePush} disabled={pushBusy}
                  className={pushOn ? "cs-btn-primary text-[13px]" : "cs-btn-ghost text-[13px]"}
                  data-testid="push-toggle">
            {pushBusy ? <Spinner /> : (pushOn ? <Bell size={16} /> : <BellOff size={16} />)}
            {pushOn ? "Push activé" : "Activer les notifications"}
          </button>
          <button onClick={markAllRead} className="cs-btn-ghost text-[13px]" data-testid="mark-all-read">
            <CheckCheck size={16} /> Tout marquer comme lu
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="cs-card p-10 flex justify-center"><Spinner /></div>
      ) : !events?.length ? (
        <EmptyState title="Aucune notification" hint="Vous serez prévenu(e) ici dès qu'un événement vous concerne." />
      ) : (
        <div className="flex flex-col gap-3" data-testid="inbox-notifications">
          {events.map((ev) => (
            <button key={ev.id} onClick={() => clickEvent(ev)}
                    className="cs-card p-4 text-left flex items-start gap-3 hover:bg-[var(--cs-bg)] transition"
                    style={{ borderLeft: ev.read ? undefined : "3px solid var(--cs-primary)" }}
                    data-testid={`event-${ev.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Chip tone="primary">{TYPE_LABEL[ev.type] || ev.type}</Chip>
                  <span className="font-semibold text-[15px]">{ev.referral_code}</span>
                  {!ev.read && <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--cs-primary-dark)" }}>Nouveau</span>}
                </div>
                <div className="text-[13px] mt-1.5" style={{ color: "var(--cs-text-muted)" }}>{ev.message}</div>
                <div className="text-[11px] mt-2" style={{ color: "var(--cs-text-subtle)" }}>{timeAgoFr(ev.created_at)}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </Page>
  );
}
